import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrganisation } from '../contexts/OrganisationContext';
import { SubscriptionPlan, PlanRegion } from '../types/api';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { CheckIcon } from '@heroicons/react/24/outline';
import { getApiErrorMessage } from '../constants/messages';

const OrganisationPlanPage: React.FC = () => {
  const { t } = (useTranslation as any)('organisations');
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const { user } = useAuth();
  const { currentOrganisation, selectOrganisation, canChangePlan } = useOrganisation();
  const [plans, setPlans] = useState<Record<string, SubscriptionPlan>>({});
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);
  const [region, setRegion] = useState<PlanRegion>('europe');

  // Prix exacts renvoyés par l'API (FCFA, différenciés par région) — aucun calcul.
  const formatPrice = (plan: SubscriptionPlan) => {
    const price = plan.price_monthly;
    if (price === null || price === undefined) return t('plan.contactUs');
    return `${price[region].toLocaleString('fr-FR')} FCFA`;
  };

  useEffect(() => {
    if (orgId && (!currentOrganisation || currentOrganisation.id !== orgId)) {
      selectOrganisation(orgId).catch(() => {
        toast.error(t('plan.loadOrgError'));
        navigate('/organisations');
      });
    }
  }, [orgId, currentOrganisation, selectOrganisation, navigate]);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const response = await apiService.getAvailablePlans();
        setPlans(response.plans);
      } catch (error) {
        toast.error(t('plan.loadError'));
      } finally {
        setLoading(false);
      }
    };
    loadPlans();
  }, []);

  const handleChangePlan = async (planType: string) => {
    if (!canChangePlan()) {
      toast.error(t('plan.onlyOwnerCanChange'));
      return;
    }

    // Deux chemins, et c'est le coeur du sujet.
    //
    // Le GRATUIT reste en libre-service : quitter un plan payant ne doit pas
    // demander un ticket, et le serveur garde sa protection (refus si la
    // consommation dépasse les quotas visés).
    //
    // Les plans PAYANTS ne se changent plus depuis ici — il n'existe ni
    // abonnement ni encaissement, les proposer revenait à les donner. Le
    // serveur répond 403. Ce bouton doit donc émettre une DEMANDE, qui
    // déclenche un mail à l'équipe commerciale ; sans ça le client se heurtait
    // au refus sans aucun moyen de faire sa demande.
    const versLeGratuit = planType.toUpperCase() === 'FREE';

    if (!versLeGratuit) {
      if (!window.confirm(t('plan.confirmRequest', { plan: planType }) as string)) return;
      setChanging(true);
      try {
        const r = await apiService.requestEnterprisePlan(currentOrganisation!.id, {
          name: [user?.prenom, user?.nom].filter(Boolean).join(' ') || user?.email || '—',
          email: user?.email || '',
          plan_type: planType.toUpperCase(),
        });
        // Le serveur DIT si la notification est réellement partie. On ne
        // transforme pas un envoi raté en « demande envoyée ».
        if (r.status === 'pending_unconfirmed') {
          toast.error(r.message);
        } else {
          toast.success(r.message || (t('plan.requestSuccess') as string));
        }
      } catch (error: unknown) {
        toast.error(getApiErrorMessage(error, t('plan.requestError') as string));
      } finally {
        setChanging(false);
      }
      return;
    }

    if (!window.confirm(t('plan.confirmChange', { plan: planType }) as string)) return;
    setChanging(true);
    try {
      await apiService.changeOrganisationPlan(currentOrganisation!.id, { new_plan_type: planType as any });
      toast.success(t('plan.changeSuccess'));
      window.location.reload();
    } catch (error: unknown) {
      // `getApiErrorMessage(error) || fallback` était du code mort : la
      // fonction renvoie toujours une chaîne non vide. Le repli se passe en
      // SECOND argument (cf. CLAUDE.md).
      toast.error(getApiErrorMessage(error, t('plan.changeError') as string));
    } finally {
      setChanging(false);
    }
  };

  if (loading || !currentOrganisation) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate(`/organisations/${orgId}/dashboard`)}
          className="text-sm text-accent-400 hover:text-accent-300 mb-2"
        >
          ← {t('plan.backToDashboard')}
        </button>
        <h1 className="text-3xl font-bold text-text-primary">
          {t('plan.title')}
        </h1>
        <p className="mt-2 text-text-secondary">
          {t('plan.currentPlan')} <span className="font-semibold">{currentOrganisation.plan_type}</span>
        </p>
      </div>

      {/* Sélecteur de région — les prix diffèrent selon la région de déploiement */}
      <div className="flex flex-col items-center gap-2 mb-8">
        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-surface-3 border border-border-1">
          {(['afrique', 'europe'] as PlanRegion[]).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRegion(r)}
              aria-pressed={region === r}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                region === r ? 'bg-accent-600 text-white' : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {t(`plan.region_${r}`)}
            </button>
          ))}
        </div>
        <p className="text-xs text-text-tertiary">{t('plan.regionHint')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(plans).map(([planName, plan]) => {
          const isCurrent = currentOrganisation.plan_type === planName;
          return (
            <div
              key={planName}
              className={`bg-surface-2 rounded-card shadow-lg p-6 ${isCurrent ? 'ring-2 ring-accent-500' : ''}`}
            >
              {isCurrent && (
                <div className="text-xs font-semibold text-accent-400 mb-2">
                  {t('plan.currentPlanBadge')}
                </div>
              )}
              <h3 className="text-2xl font-bold text-text-primary">{planName}</h3>
              <div className="mt-4 flex items-baseline text-text-primary">
                <span className="text-4xl font-extrabold">
                  {formatPrice(plan)}
                </span>
                {planName !== 'FREE' && planName !== 'ENTERPRISE' && (
                  <span className="ml-1 text-xl text-text-tertiary">{t('plan.perMonth')}</span>
                )}
              </div>
              <ul className="mt-6 space-y-3">
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">{plan.quota_cpu} vCPU</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">{plan.quota_ram} GB RAM</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">{plan.quota_storage} GB Storage</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">
                    {plan.max_projects === -1 ? t('plan.unlimitedProjects') : t('plan.projectCount', { count: plan.max_projects })}
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">
                    {plan.max_members === -1 ? t('plan.unlimitedMembers') : t('plan.memberCount', { count: plan.max_members })}
                  </span>
                </li>
              </ul>
              {!isCurrent && plan.selectable && canChangePlan() && (
                <button
                  onClick={() => handleChangePlan(planName)}
                  disabled={changing}
                  className="mt-6 w-full bg-accent-600 text-white py-2 px-4 rounded-md hover:bg-accent-700 disabled:opacity-50"
                >
                  {changing ? t('plan.changing') : t('plan.choosePlan')}
                </button>
              )}
              {!plan.selectable && (
                <button
                  disabled
                  className="mt-6 w-full bg-surface-3 text-text-tertiary py-2 px-4 rounded-md cursor-not-allowed"
                >
                  {t('plan.contactSales')}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrganisationPlanPage;
